import store from 'store';
import axios from 'axios';
import http from './HttpService';
import { getCurrentTime } from '../Analytics/utils';
import { API_BASE_ANALYTICS, API_BASE_BUYER_JOURNEY, API_BASE_CAMPAIGN } from '../../constants/index';
import { getAnalyticsFilterId, getQueryIsTestEmail, getQueryWorkflowId } from 'modules/EmailBrowser/utils';
import { ISearchedEmailDropdownList } from 'modules/Analytics/interfaces';
import { unselectAllEmails } from 'modules/Analytics/Dashboard/actions';
import { objectToQueryParams } from 'utils';

const addCampaignsEmailPayload = (
  payload: FormData,
  allCampaigns: boolean,
  campaignIds: number[],
  emailIds: number[],
  excludeEmailIds: number[],
  selectAll: boolean,
  unSelectAll: boolean,
  searchedEmailList: Array<ISearchedEmailDropdownList>,
  isTestEmailFilter: boolean,
) => {
  if (allCampaigns && !unSelectAll) {
    payload.append('all_campaigns', 'true');
    payload.append('campaign_ids', JSON.stringify([]));
  } else {
    payload.append('campaign_ids', JSON.stringify(campaignIds.length ? campaignIds.join().split(',') : []));
    payload.append('all_campaigns', 'false');
  }
  payload.append('include_emails', JSON.stringify(emailIds.length && unSelectAll ? emailIds.join().split(',') : []));
  payload.append(
    'exclude_emails',
    JSON.stringify(excludeEmailIds.length && selectAll ? excludeEmailIds.join().split(',') : []),
  );
  payload.append('unselect_all', unSelectAll ? 'true' : 'false');
  payload.append('select_all', selectAll ? 'true' : 'false');
  payload.append('is_test_email', isTestEmailFilter || getQueryIsTestEmail() ? 'true' : 'false');
  let list = Array<any>();
  if (searchedEmailList.length) {
    list = searchedEmailList.map((item: ISearchedEmailDropdownList) => {
      return {
        term: item.term,
        select_all: item.selectAll,
        email_ids: item.emailIds.length ? item.emailIds.join().split(',') : [],
      };
    });
  }
  payload.append('search_list', JSON.stringify(list.length ? list : []));
};

const addWorkflowsPayload = (
  payload: FormData,
  workflowIds: number[],
  emailIds: number[],
  excludeEmailIds: number[],
  selectAll: boolean,
  unSelectAll: boolean,
  searchedEmailList: Array<ISearchedEmailDropdownList>,
) => {
  let isWorkflow = store.getState().analyticsDashboard.appliedIsWorkflow;
  if (isWorkflow || getQueryWorkflowId()) {
    if (workflowIds?.length > 0) {
      payload.append('all_workflows', '0');
      for (let wid of workflowIds) {
        payload.append('workflow_ids', wid.toString());
      }
      payload.delete('campaign_ids');
    }
    payload.append('include_emails', JSON.stringify(emailIds.length && unSelectAll ? emailIds.join().split(',') : []));
    payload.append(
      'exclude_emails',
      JSON.stringify(excludeEmailIds.length && selectAll ? excludeEmailIds.join().split(',') : []),
    );
    payload.append('unselect_all', unSelectAll ? 'true' : 'false');
    payload.append('select_all', selectAll ? 'true' : 'false');
    let list = Array<any>();
    if (searchedEmailList.length) {
      list = searchedEmailList.map((item: ISearchedEmailDropdownList) => {
        return {
          term: item.term,
          select_all: item.selectAll,
          email_ids: item.emailIds.length ? item.emailIds.join().split(',') : [],
        };
      });
    }
    payload.append('search_list', JSON.stringify(list.length ? list : []));
  }
};

const addAndCheckFilterIdPayload = (
  payload: FormData,
  workflowIds: number[],
  campaignIds: number[],
  emailIds: number[],
  allCampaigns: boolean,
) => {
  const filterId = getAnalyticsFilterId();
  if (filterId) {
    if (campaignIds?.length > 0 || emailIds?.length > 0 || workflowIds?.length > 0) {
      return true;
    } else {
      payload.append('filterId', filterId.toString());
      return false;
    }
  }
  return true;
};

let cancelTokenOverview: any;
export const analyticsOverviewService = async (params: any, overview?: boolean) => {
  const payload = new FormData();
  let isTestEmailFilter = store.getState().analyticsDashboard.isTestEmailFilter;
  payload.append('advanced_search', 'true');
  if (
    addAndCheckFilterIdPayload(payload, params.workflowIds, params.campaignIds, params.emailIds, params.allCampaigns)
  ) {
    if (params.startDate) payload.append('start_date', params.startDate);
    if (params.endDate) payload.append('end_date', params.endDate);
    if (params?.isWorkflow) {
      addWorkflowsPayload(
        payload,
        params.workflowIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
      );
    } else {
      addCampaignsEmailPayload(
        payload,
        params.allCampaigns,
        params.campaignIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
        isTestEmailFilter,
      );
    }
  }
  payload.append('get_overview_data', '1');
  if (params.exportAnalytics) payload.append('export_analytics', 'true');
  payload.append('search_creation_datetime', getCurrentTime());

  //Check if there are any previous pending requests
  if (typeof cancelTokenOverview != typeof undefined) {
    cancelTokenOverview && cancelTokenOverview.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenOverview = axios.CancelToken.source();

  return await http.post(`${API_BASE_ANALYTICS}analytics_dashboard/`, payload, {
    cancelToken: cancelTokenOverview.token,
  });
};

let cancelTokenDashboard: any;
export const analyticsDashboardService = async (params: any) => {
  const payload = new FormData();
  let isTestEmailFilter = store.getState().analyticsDashboard.isTestEmailFilter;

  payload.append('advanced_search', 'true');

  if (
    addAndCheckFilterIdPayload(payload, params.workflowIds, params.campaignIds, params.emailIds, params.allCampaigns)
  ) {
    if (params.startDate) payload.append('start_date', params.startDate);
    if (params.endDate) payload.append('end_date', params.endDate);
    if (params?.isWorkflow) {
      addWorkflowsPayload(
        payload,
        params.workflowIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
      );
    } else {
      addCampaignsEmailPayload(
        payload,
        params.allCampaigns,
        params.campaignIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
        isTestEmailFilter,
      );
    }
  }
  if (params.exportAnalytics) payload.append('export_analytics', 'true');
  payload.append('search_creation_datetime', getCurrentTime());

  //Check if there are any previous pending requests
  if (typeof cancelTokenDashboard != typeof undefined) {
    cancelTokenDashboard && cancelTokenDashboard.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenDashboard = axios.CancelToken.source();

  return await http.post(`${API_BASE_ANALYTICS}analytics_dashboard/`, payload, {
    cancelToken: cancelTokenDashboard.token,
  });
};

export const getAnalyticsAssetsListService = async () => {
  return await http.get(`${API_BASE_CAMPAIGN}get_asset_browser_content/`, {});
};

let cancelTokenGraphData: any;
export const getAnalyticsGraphDataService = async (params: any) => {
  const payload = new FormData();
  let isTestEmailFilter = store.getState().analyticsDashboard.isTestEmailFilter;
  if (
    addAndCheckFilterIdPayload(payload, params.workflowIds, params.campaignIds, params.emailIds, params.allCampaigns)
  ) {
    if (params.startDate) payload.append('start_date', params.startDate);
    if (params.endDate) payload.append('end_date', params.endDate);
    if (params?.isWorkflow) {
      addWorkflowsPayload(
        payload,
        params.workflowIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
      );
    } else {
      addCampaignsEmailPayload(
        payload,
        params.allCampaigns,
        params.campaignIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
        isTestEmailFilter,
      );
    }
  }
  payload.append('analytic_request', '1');
  if (params.exportAnalytics) payload.append('export_analytics', params.exportAnalytics);
  //Check if there are any previous pending requests
  if (typeof cancelTokenGraphData != typeof undefined) {
    cancelTokenGraphData && cancelTokenGraphData.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenGraphData = axios.CancelToken.source();

  return await http.post(
    `${API_BASE_ANALYTICS}campaigns_analytics/`,
    payload,
    { cancelToken: cancelTokenGraphData.token }, //Pass the cancel token to the current request
  );
};

let cancelTokenEmailGraphData: any;
export const getAnalyticsEmailGraphDataService = async (params: any) => {
  const payload = new FormData();
  let isTestEmailFilter = store.getState().analyticsDashboard.isTestEmailFilter;
  if (
    addAndCheckFilterIdPayload(payload, params.workflowIds, params.campaignIds, params.emailIds, params.allCampaigns)
  ) {
    if (params.startDate) payload.append('start_date', params.startDate);
    if (params.endDate) payload.append('end_date', params.endDate);
    if (params?.workflowIds?.length) {
      addWorkflowsPayload(
        payload,
        params.workflowIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
      );
    } else {
      addCampaignsEmailPayload(
        payload,
        params.allCampaigns,
        params.campaignIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
        isTestEmailFilter,
      );
    }
  }
  payload.append('analytic_request', '1');
  if (params.exportAnalytics) payload.append('export_analytics', params.exportAnalytics);
  //Check if there are any previous pending requests
  if (typeof cancelTokenEmailGraphData != typeof undefined) {
    cancelTokenEmailGraphData && cancelTokenEmailGraphData.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenEmailGraphData = axios.CancelToken.source();

  return await http.post(
    `${API_BASE_ANALYTICS}dashboard_email_graph/`,
    payload,
    { cancelToken: cancelTokenEmailGraphData.token }, //Pass the cancel token to the current request
  );
};

let cancelTokenCammpaignGraphData: any;
export const getAnalyticsCampaignGraphDataService = async (params: any) => {
  const payload = new FormData();
  let isTestEmailFilter = store.getState().analyticsDashboard.isTestEmailFilter;
  if (
    addAndCheckFilterIdPayload(payload, params.workflowIds, params.campaignIds, params.emailIds, params.allCampaigns)
  ) {
    if (params.startDate) payload.append('start_date', params.startDate);
    if (params.endDate) payload.append('end_date', params.endDate);
    if (params?.workflowIds?.length) {
      addWorkflowsPayload(
        payload,
        params.workflowIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
      );
    } else {
      addCampaignsEmailPayload(
        payload,
        params.allCampaigns,
        params.campaignIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
        isTestEmailFilter,
      );
    }
  }
  payload.append('analytic_request', '1');
  if (params.exportAnalytics) payload.append('export_analytics', params.exportAnalytics);
  //Check if there are any previous pending requests
  if (typeof cancelTokenCammpaignGraphData != typeof undefined) {
    cancelTokenCammpaignGraphData && cancelTokenCammpaignGraphData.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenCammpaignGraphData = axios.CancelToken.source();

  return await http.post(
    `${API_BASE_ANALYTICS}dashboard_campaign_graph/`,
    payload,
    { cancelToken: cancelTokenCammpaignGraphData.token }, //Pass the cancel token to the current request
  );
};

let cancelTokenCampaignAsset: any;
export const getAnalyticsCampaignsAssetsListService = async () => {
  //Check if there are any previous pending requests
  if (typeof cancelTokenCampaignAsset != typeof undefined) {
    cancelTokenCampaignAsset && cancelTokenCampaignAsset.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenCampaignAsset = axios.CancelToken.source();

  return await http.get(`${API_BASE_ANALYTICS}dashboard_campaign_emails/`, {
    cancelToken: cancelTokenCampaignAsset.token,
  });
};

export const getAnalyticsWorkflowListService = async () => {
  return await http.get(`${API_BASE_ANALYTICS}dashboard_workflows/`);
};

let cancelTokenCampaigns: any;
export const getAnalyticsSelectedCampaignsAssetsListService = async (campaignIds: string | any[] | undefined = []) => {
  let payload = new FormData();
  if (campaignIds) {
    for (let cid of campaignIds) {
      payload.append('campaign_ids', cid);
    }
  }
  //Check if there are any previous pending requests
  if (typeof cancelTokenCampaigns != typeof undefined) {
    cancelTokenCampaigns && cancelTokenCampaigns.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenCampaigns = axios.CancelToken.source();

  return await http.post(`${API_BASE_ANALYTICS}dashboard_campaign_emails/`, payload, {
    cancelToken: cancelTokenCampaigns.token,
  });
};

export const exportAnalyticsDataService = async (campaign_ids: any[], asset_ids: any[], ssid: any) => {
  const payload = new FormData();
  payload.append('action', 'overview_stats_cards');
  payload.append('campaign_id', '');
  payload.append('asset_id', campaign_ids.join(','));
  payload.append('version_id', asset_ids.join(','));
  payload.append('saved_search_id', ssid);
  payload.append('time_diff', '');
  payload.append('export_analytics', 'true');

  return await http.post(`${API_BASE_ANALYTICS}export/`, payload);
};

let cancelTokenContactAnalytics: any;
//Contacts analytics
export const getContactsListService = async (params: any, isAccounts?: boolean) => {
  const payload = new FormData();
  let isTestEmailFilter = store.getState().analyticsDashboard.isTestEmailFilter;

  if (params.tab) payload.append('tab', params.tab);
  if (params.limit !== undefined) payload.append('limit', params.limit);
  if (params.offset !== undefined) payload.append('offset', params.offset);
  if (params.searchWord !== undefined && params.searchWord.trim() !== '')
    payload.append('search_word', params.searchWord);
  if (params.sortType && params.sortType !== 'none') {
    payload.append('sort_type', params.sortType);
    payload.append('sort_by', params.sortBy);
  }
  if (
    addAndCheckFilterIdPayload(payload, params.workflowIds, params.campaignIds, params.emailIds, params.allCampaigns)
  ) {
    payload.append('start_date', params.startDate);
    payload.append('end_date', params.endDate);
    if (params?.workflowIds?.length) {
      addWorkflowsPayload(
        payload,
        params.workflowIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
      );
    } else {
      addCampaignsEmailPayload(
        payload,
        params.allCampaigns,
        params.campaignIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
        isTestEmailFilter,
      );
    }
  }

  if (params.accountId !== undefined) payload.append('account_id', params.accountId);
  if (params.contactsByFilter) payload.append('contacts_by_filter', params.contactsByFilter);
  if (params.exportAnalytics) payload.append('export_analytics', 'true');

  if (isAccounts) {
    //Check if there are any previous pending requests
    if (typeof cancelTokenContactAnalytics != typeof undefined) {
      cancelTokenContactAnalytics && cancelTokenContactAnalytics.cancel('Operation canceled due to new request.');
    }
    //Save the cancel token for the current request
    cancelTokenContactAnalytics = axios.CancelToken.source();

    return await http.post(
      `${API_BASE_ANALYTICS}contacts_listing/`,
      payload,
      { cancelToken: cancelTokenContactAnalytics.token }, //Pass the cancel token to the current request
    );
  }
  return await http.post(`${API_BASE_ANALYTICS}contacts_listing/`, payload);
};

let cancelTokenContactContent: any;
//content articles for contacts in analytics
export const getContentArticlesContactsService = async (params: any) => {
  const payload = new FormData();
  payload.append('contact_id', params.contact_id);
  payload.append('limit', params.limit);
  payload.append('offset', params.offset);
  if (params.sort_type && params.sort_type !== 'none') {
    payload.append('order_by', params.sort_type);
    payload.append('sort_by', params.sort_by);
  }

  //Check if there are any previous pending requests
  if (typeof cancelTokenContactContent != typeof undefined) {
    cancelTokenContactContent && cancelTokenContactContent.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenContactContent = axios.CancelToken.source();

  return await http.post(`${API_BASE_ANALYTICS}contact_content_engagement/`, payload, {
    cancelToken: cancelTokenContactContent.token,
  });
};

let cancelTokenContactDashboard: any;
//contact dashboard for analytics
export const getContactDashboardService = async (params: any) => {
  const payload = new FormData();
  payload.append('contact_id', params.contact_id);
  payload.append('limit', params.limit);
  payload.append('offset', params.offset);

  //Check if there are any previous pending requests
  if (typeof cancelTokenContactDashboard != typeof undefined) {
    cancelTokenContactDashboard && cancelTokenContactDashboard.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenContactDashboard = axios.CancelToken.source();

  return await http.post(`${API_BASE_ANALYTICS}contact_dashboard/`, payload, {
    cancelToken: cancelTokenContactDashboard.token,
  });
};

let cancelTokenAccountAnalytics: any;
export const getAccountsListService = async (params: any) => {
  const payload = new FormData();
  let isTestEmailFilter = store.getState().analyticsDashboard.isTestEmailFilter;
  if (
    addAndCheckFilterIdPayload(payload, params.workflowIds, params.campaignIds, params.emailIds, params.allCampaigns)
  ) {
    if (params.startDate) payload.append('start_date', params.startDate);
    if (params.endDate) payload.append('end_date', params.endDate);
    if (params?.workflowIds?.length) {
      addWorkflowsPayload(
        payload,
        params.workflowIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
      );
    } else {
      addCampaignsEmailPayload(
        payload,
        params.allCampaigns,
        params.campaignIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
        isTestEmailFilter,
      );
    }
  }

  if (params.limit !== undefined) payload.append('limit', params.limit);
  if (params.offset !== undefined) payload.append('offset', params.offset);
  if (params.sortBy && params.sortBy !== 'none') {
    payload.append('sort_by', params.sortBy);
    payload.append('order_by', params.orderBy);
  }
  if (params.searchWord !== undefined && params.searchWord !== '') payload.append('search_account', params.searchWord);
  if (params.exportAnalytics) payload.append('export_analytics', 'true');

  //Check if there are any previous pending requests
  if (typeof cancelTokenAccountAnalytics != typeof undefined) {
    cancelTokenAccountAnalytics && cancelTokenAccountAnalytics.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenAccountAnalytics = axios.CancelToken.source();

  return await http.post(`${API_BASE_ANALYTICS}accounts_listing/`, payload, {
    cancelToken: cancelTokenAccountAnalytics.token,
  });
};

let cancelTokenContentAnalytics: any;
export const getContentListService = async (params: any) => {
  const payload = new FormData();
  let isTestEmailFilter = store.getState().analyticsDashboard.isTestEmailFilter;

  if (
    addAndCheckFilterIdPayload(payload, params.workflowIds, params.campaignIds, params.emailIds, params.allCampaigns)
  ) {
    payload.append('start_date', params.startDate);
    payload.append('end_date', params.endDate);
    if (params?.workflowIds?.length) {
      addWorkflowsPayload(
        payload,
        params.workflowIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
      );
    } else {
      addCampaignsEmailPayload(
        payload,
        params.allCampaigns,
        params.campaignIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
        isTestEmailFilter,
      );
    }
  }

  if (params.limit !== undefined) payload.append('limit', params.limit);
  if (params.offset !== undefined) payload.append('offset', params.offset);
  if (params.searchWord !== undefined && params.searchWord !== '') payload.append('search_content', params.searchWord);
  if (params.sortBy && params.sortBy !== 'none') {
    payload.append('sort_by', params.sortBy);
    payload.append('order_by', params.orderBy);
  }
  if (params.exportAnalytics) payload.append('export_analytics', 'true');

  //Check if there are any previous pending requests
  if (typeof cancelTokenContentAnalytics != typeof undefined) {
    cancelTokenContentAnalytics && cancelTokenContentAnalytics.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenContentAnalytics = axios.CancelToken.source();

  return await http.post(`${API_BASE_ANALYTICS}content_listing/`, payload, {
    cancelToken: cancelTokenContentAnalytics.token,
  });
};

let cancelTokenContentEngagement: any;
export const getContentEngagementListService = async (params: any) => {
  const payload = new FormData();
  const isTestEmailFilter = store.getState().analyticsDashboard.isTestEmailFilter;
  payload.append('account_id', params.accountId);
  if (params.limit !== undefined) payload.append('limit', params.limit);
  if (params.offset !== undefined) payload.append('offset', params.offset);
  if (params.searchWord) payload.append('search_term', params.searchWord);
  if (params.sortBy) payload.append('sort_by', params.sortBy);
  if (params.orderBy !== undefined && params.orderBy !== 'none') payload.append('order_by', params.orderBy);
  if (
    addAndCheckFilterIdPayload(payload, params.workflowIds, params.campaignIds, params.emailIds, params.allCampaigns)
  ) {
    if (params.startDate) payload.append('start_date', params.startDate);
    if (params.endDate) payload.append('end_date', params.endDate);
    if (params?.workflowIds?.length) {
      addWorkflowsPayload(
        payload,
        params.workflowIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
      );
    } else {
      addCampaignsEmailPayload(
        payload,
        params.allCampaigns,
        params.campaignIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
        isTestEmailFilter,
      );
    }
  }
  if (params.exportAnalytics) payload.append('export_analytics', 'true');

  //Check if there are any previous pending requests
  if (typeof cancelTokenContentEngagement != typeof undefined) {
    cancelTokenContentEngagement && cancelTokenContentEngagement.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenContentEngagement = axios.CancelToken.source();

  return await http.post(`${API_BASE_ANALYTICS}account_content_engagement/`, payload, {
    cancelToken: cancelTokenContentEngagement.token,
  });
};

let cancelTokenContactsEmailEngagement: any;
export const getContactsEmailEngagementService = async (params: any) => {
  const payload = new FormData();
  const isTestEmailFilter = store.getState().analyticsDashboard.isTestEmailFilter;
  if (params.contact_id) payload.append('contact_id', params.contact_id);
  if (params.limit !== undefined) payload.append('limit', params.limit);
  if (params.offset !== undefined) payload.append('offset', params.offset);
  if (params.search_term) payload.append('search_term', params.search_term);
  if (params.status) payload.append('status', params.status);
  if (params.sort_by) payload.append('sort_by', params.sort_by);
  if (params.sort_type) payload.append('sort_type', params.sort_type);
  if (params.start_date) payload.append('start_date', params.start_date);
  if (params.end_date) payload.append('end_date', params.end_date);
  if (params.email_by_total_opens) payload.append('email_by_total_opens', params.email_by_total_opens);
  if (params.email_by_total_clicks) payload.append('email_by_total_clicks', params.email_by_total_clicks);
  if (params.export) payload.append('export_analytics', params.export);
  if (
    addAndCheckFilterIdPayload(payload, params.workflowIds, params.campaignIds, params.emailIds, params.allCampaigns)
  ) {
    // addCampaignsEmailPayload(
    //   payload,
    //   params.allCampaigns,
    //   params.campaignIds,
    //   params.emailIds,
    //   params.excludeAssetsIds,
    //   params.selectAll,
    //   params.unSelectAll,
    //   params.searchedEmailList,
    //   isTestEmailFilter,
    // );
    // addWorkflowsPayload(payload, params.workflowIds);
    // addAnalyticsFiltersDropdownDataInPayload(payload);
  }

  //Check if there are any previous pending requests
  if (typeof cancelTokenContactsEmailEngagement != typeof undefined) {
    cancelTokenContactsEmailEngagement &&
      cancelTokenContactsEmailEngagement.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenContactsEmailEngagement = axios.CancelToken.source();

  return await http.post(`${API_BASE_ANALYTICS}contact_email_engagement/`, payload, {
    cancelToken: cancelTokenContactsEmailEngagement.token,
  });
};
let cancelTokenAccountsEmailEngagement: any;
export const getAccountsEmailEngagementService = async (params: any) => {
  const payload = new FormData();
  const isTestEmailFilter = store.getState().analyticsDashboard.isTestEmailFilter;
  if (params.account_id !== undefined) payload.append('account_id', params.account_id);
  if (params.limit !== undefined) payload.append('limit', params.limit);
  if (params.offset !== undefined) payload.append('offset', params.offset);
  if (params.search_term) payload.append('search_term', params.search_term);
  if (params.sort_by) payload.append('sort_by', params.sort_by);
  if (params.sort_type) payload.append('sort_type', params.sort_type);
  if (params.start_date) payload.append('start_date', params.start_date);
  if (params.end_date) payload.append('end_date', params.end_date);
  if (params.email_by_unique_opens) payload.append('email_by_unique_opens', params.email_by_unique_opens);
  if (params.email_by_unique_clicks) payload.append('email_by_unique_clicks', params.email_by_unique_clicks);
  if (params.export) payload.append('export_analytics', params.export);
  if (
    addAndCheckFilterIdPayload(payload, params.workflowIds, params.campaignIds, params.emailIds, params.allCampaigns)
  ) {
    if (params?.workflowIds?.length) {
      addWorkflowsPayload(
        payload,
        params.workflowIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
      );
    } else {
      addCampaignsEmailPayload(
        payload,
        params.allCampaigns,
        params.campaignIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
        isTestEmailFilter,
      );
    }
  }

  //Check if there are any previous pending requests
  if (typeof cancelTokenAccountsEmailEngagement != typeof undefined) {
    cancelTokenAccountsEmailEngagement &&
      cancelTokenAccountsEmailEngagement.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenAccountsEmailEngagement = axios.CancelToken.source();

  return await http.post(`${API_BASE_ANALYTICS}account_email_engagement/`, payload, {
    cancelToken: cancelTokenAccountsEmailEngagement.token,
  });
};

let cancelTokenAccountDetails: any;
export const getAccountsDashboardService = async (params: any) => {
  const isTestEmailFilter = store.getState().analyticsDashboard.isTestEmailFilter;
  const payload = new FormData();
  payload.append('account_id', params.accountId);
  if (
    addAndCheckFilterIdPayload(payload, params.workflowIds, params.campaignIds, params.emailIds, params.allCampaigns)
  ) {
    if (params.startDate && params.endDate) {
      payload.append('start_date', params.startDate);
      payload.append('end_date', params.endDate);
    }
    if (params?.workflowIds?.length) {
      addWorkflowsPayload(
        payload,
        params.workflowIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
      );
    } else {
      addCampaignsEmailPayload(
        payload,
        params.allCampaigns,
        params.campaignIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
        isTestEmailFilter,
      );
    }
  }
  //Check if there are any previous pending requests
  if (typeof cancelTokenAccountDetails != typeof undefined) {
    cancelTokenAccountDetails && cancelTokenAccountDetails.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenAccountDetails = axios.CancelToken.source();

  return await http.post(`${API_BASE_ANALYTICS}account_details/`, payload, {
    cancelToken: cancelTokenAccountDetails.token,
  });
};

let cancelTokenDeliveredContactAnalytics: any;
//Contacts analytics
export const getDeliveredContactsListService = async (params: any, isAccounts?: boolean) => {
  const payload = new FormData();
  let isTestEmailFilter = store.getState().analyticsDashboard.isTestEmailFilter;

  if (params.tab) payload.append('tab', params.tab);
  if (params.limit !== undefined) payload.append('limit', params.limit);
  if (params.offset !== undefined) payload.append('offset', params.offset);
  if (params.searchWord !== undefined && params.searchWord.trim() !== '')
    payload.append('search_word', params.searchWord);
  if (params.sortType && params.sortType !== 'none') {
    payload.append('sort_type', params.sortType);
    payload.append('sort_by', params.sortBy);
  }

  if (
    addAndCheckFilterIdPayload(payload, params.workflowIds, params.campaignIds, params.emailIds, params.allCampaigns)
  ) {
    payload.append('start_date', params.startDate);
    payload.append('end_date', params.endDate);
    if (params?.workflowIds?.length) {
      addWorkflowsPayload(
        payload,
        params.workflowIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
      );
    } else {
      addCampaignsEmailPayload(
        payload,
        params.allCampaigns,
        params.campaignIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
        isTestEmailFilter,
      );
    }
  }
  if (params.accountId !== undefined && isAccounts) payload.append('account_id', params.accountId);
  if (params.contactsByFilter) payload.append('contacts_by_filter', params.contactsByFilter);
  if (params.bounceFilter) payload.append('bounce_filter', params.bounceFilter);
  if (params.exportAnalytics) payload.append('export_analytics', 'true');
  if (params?.articleId) payload.append('article_id', params.articleId);
  if (params?.articleLink) payload.append('article_url', params.articleLink);
  if (params?.createNewList) {
    payload.append('list_create', 'true');
    payload.append('list_name', params.listName);
  }

  //Check if there are any previous pending requests
  if (typeof cancelTokenDeliveredContactAnalytics != typeof undefined) {
    cancelTokenDeliveredContactAnalytics &&
      cancelTokenDeliveredContactAnalytics.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenDeliveredContactAnalytics = axios.CancelToken.source();

  return await http.post(
    `${API_BASE_ANALYTICS}contacts_listing/`,
    payload,
    { cancelToken: cancelTokenDeliveredContactAnalytics.token }, //Pass the cancel token to the current request
  );
};

let cancelBouncedTokenContactAnalytics: any;
//Contacts analytics
export const getBouncedContactsListService = async (params: any, isAccounts?: boolean) => {
  const payload = new FormData();
  let isTestEmailFilter = store.getState().analyticsDashboard.isTestEmailFilter;

  if (params.tab) payload.append('tab', params.tab);
  if (params.limit !== undefined) payload.append('limit', params.limit);
  if (params.offset !== undefined) payload.append('offset', params.offset);
  if (params.searchWord !== undefined && params.searchWord.trim() !== '')
    payload.append('search_word', params.searchWord);
  if (params.sortType && params.sortType !== 'none') {
    payload.append('sort_type', params.sortType);
    payload.append('sort_by', params.sortBy);
  }

  if (
    addAndCheckFilterIdPayload(payload, params.workflowIds, params.campaignIds, params.emailIds, params.allCampaigns)
  ) {
    payload.append('start_date', params.startDate);
    payload.append('end_date', params.endDate);
    if (params?.workflowIds?.length) {
      addWorkflowsPayload(
        payload,
        params.workflowIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
      );
    } else {
      addCampaignsEmailPayload(
        payload,
        params.allCampaigns,
        params.campaignIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
        isTestEmailFilter,
      );
    }
  }
  if (params.accountId !== undefined && isAccounts) payload.append('account_id', params.accountId);
  if (params.contactsByFilter) payload.append('contacts_by_filter', params.contactsByFilter);
  if (params.exportAnalytics) payload.append('export_analytics', 'true');
  if (params.bounce_filter) payload.append('bounce_filter', params.bounce_filter);

  //Check if there are any previous pending requests
  if (typeof cancelBouncedTokenContactAnalytics != typeof undefined) {
    cancelBouncedTokenContactAnalytics &&
      cancelBouncedTokenContactAnalytics.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelBouncedTokenContactAnalytics = axios.CancelToken.source();

  return await http.post(
    `${API_BASE_ANALYTICS}contacts_listing/`,
    payload,
    { cancelToken: cancelBouncedTokenContactAnalytics.token }, //Pass the cancel token to the current request
  );
};

let cancelTokenUnsubscribedContactAnalytics: any;
//Contacts analytics
export const getUnsubscribedContactsListService = async (params: any, isAccounts?: boolean) => {
  const payload = new FormData();
  let isTestEmailFilter = store.getState().analyticsDashboard.isTestEmailFilter;

  if (params.tab) payload.append('tab', params.tab);
  if (params.limit !== undefined) payload.append('limit', params.limit);
  if (params.offset !== undefined) payload.append('offset', params.offset);
  if (params.searchWord !== undefined && params.searchWord.trim() !== '')
    payload.append('search_word', params.searchWord);
  if (params.sortType && params.sortType !== 'none') {
    payload.append('sort_type', params.sortType);
    payload.append('sort_by', params.sortBy);
  }

  if (
    addAndCheckFilterIdPayload(payload, params.workflowIds, params.campaignIds, params.emailIds, params.allCampaigns)
  ) {
    payload.append('start_date', params.startDate);
    payload.append('end_date', params.endDate);
    if (params?.workflowIds?.length) {
      addWorkflowsPayload(
        payload,
        params.workflowIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
      );
    } else {
      addCampaignsEmailPayload(
        payload,
        params.allCampaigns,
        params.campaignIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
        isTestEmailFilter,
      );
    }
  }
  if (params.accountId !== undefined && isAccounts) payload.append('account_id', params.accountId);
  if (params.contactsByFilter) payload.append('contacts_by_filter', params.contactsByFilter);
  if (params.exportAnalytics) payload.append('export_analytics', 'true');

  //Check if there are any previous pending requests
  if (typeof cancelTokenUnsubscribedContactAnalytics != typeof undefined) {
    cancelTokenUnsubscribedContactAnalytics &&
      cancelTokenUnsubscribedContactAnalytics.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenUnsubscribedContactAnalytics = axios.CancelToken.source();

  return await http.post(
    `${API_BASE_ANALYTICS}contacts_listing/`,
    payload,
    { cancelToken: cancelTokenUnsubscribedContactAnalytics.token }, //Pass the cancel token to the current request
  );
};

let cancelTokenGetEmailTrendsService: any;

export const getEmailTrendsService = async (params: any) => {
  const payload = new FormData();
  let isTestEmailFilter = store.getState().analyticsDashboard.isTestEmailFilter;

  if (
    addAndCheckFilterIdPayload(payload, params.workflowIds, params.campaignIds, params.emailIds, params.allCampaigns)
  ) {
    payload.append('start_date', params.startDate);
    payload.append('end_date', params.endDate);
    if (params?.workflowIds?.length) {
      addWorkflowsPayload(
        payload,
        params.workflowIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
      );
    } else {
      addCampaignsEmailPayload(
        payload,
        params.allCampaigns,
        params.campaignIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
        isTestEmailFilter,
      );
    }
  }

  //Check if there are any previous pending requests
  if (typeof cancelTokenGetEmailTrendsService != typeof undefined) {
    cancelTokenGetEmailTrendsService &&
      cancelTokenGetEmailTrendsService.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenGetEmailTrendsService = axios.CancelToken.source();

  if (params.exportAnalytics) payload.append('export_analytics', 'true');

  return await http.post(
    `${API_BASE_ANALYTICS}email_trends_graph/`,
    payload,
    { cancelToken: cancelTokenGetEmailTrendsService.token }, //Pass the cancel token to the current request
  );
};

let cancelTokenGetClickTrendsService: any;

export const getClickTrendsService = async (params: any) => {
  const payload = new FormData();
  let isTestEmailFilter = store.getState().analyticsDashboard.isTestEmailFilter;
  if (
    addAndCheckFilterIdPayload(payload, params.workflowIds, params.campaignIds, params.emailIds, params.allCampaigns)
  ) {
    payload.append('start_date', params.startDate);
    payload.append('end_date', params.endDate);
    if (params?.workflowIds?.length) {
      addWorkflowsPayload(
        payload,
        params.workflowIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
      );
    } else {
      addCampaignsEmailPayload(
        payload,
        params.allCampaigns,
        params.campaignIds,
        params.emailIds,
        params.excludeAssetsIds,
        params.selectAll,
        params.unSelectAll,
        params.searchedEmailList,
        isTestEmailFilter,
      );
    }
  }

  //Check if there are any previous pending requests
  if (typeof cancelTokenGetClickTrendsService != typeof undefined) {
    cancelTokenGetClickTrendsService &&
      cancelTokenGetClickTrendsService.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenGetClickTrendsService = axios.CancelToken.source();

  if (params.exportAnalytics) payload.append('export_analytics', 'true');

  return await http.post(
    `${API_BASE_ANALYTICS}click_trends_graph/`,
    payload,
    { cancelToken: cancelTokenGetClickTrendsService.token }, //Pass the cancel token to the current request
  );
};

let cancelTokenSaveAnalyticsFilterService: any;
export const saveAnalyticsFilterService = async (params: any) => {
  let payload: any = {};
  payload.name = 'temporary_saved_filter';
  payload.saved_data = JSON.parse(params);
  payload.is_temporary = true;
  //Check if there are any previous pending requests
  if (typeof cancelTokenSaveAnalyticsFilterService != typeof undefined) {
    cancelTokenSaveAnalyticsFilterService &&
      cancelTokenSaveAnalyticsFilterService.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenSaveAnalyticsFilterService = axios.CancelToken.source();
  return await http.post(
    `${API_BASE_ANALYTICS}save_filter/`,
    payload,
    { cancelToken: cancelTokenSaveAnalyticsFilterService.token }, //Pass the cancel token to the current request
  );
};

let cancelTokenGetAnalyticsFilterService: any;
export const getAnalyticsFilterService = async (fs_id: any) => {
  //Check if there are any previous pending requests
  if (typeof cancelTokenGetAnalyticsFilterService != typeof undefined) {
    cancelTokenGetAnalyticsFilterService &&
      cancelTokenGetAnalyticsFilterService.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenGetAnalyticsFilterService = axios.CancelToken.source();
  return await http.get(
    `${API_BASE_ANALYTICS}save_filter/?filterId=${fs_id}`,
    { cancelToken: cancelTokenGetAnalyticsFilterService.token }, //Pass the cancel token to the current request
  );
};

let cancelTokenGetContactsCountService: any;
export const getContactsCountService = async (params: any) => {
  const payload = new FormData();
  if (params.tab) payload.append('tab', params.tab);
  if (params.limit !== undefined) payload.append('limit', params.limit);
  if (params.offset !== undefined) payload.append('offset', params.offset);
  if (params.searchWord !== undefined && params.searchWord.trim() !== '')
    payload.append('search_word', params.searchWord);
  if (params.sortType && params.sortType !== 'none') {
    payload.append('sort_type', params.sortType);
    payload.append('sort_by', params.sortBy);
  }
  // addCampaignsEmailPayload(
  //   payload,
  //   params.allCampaigns,
  //   {
  //     excludeCampaignsIds: params.excludeCampaignsIds,
  //     excludeAssetsIds: params.excludeAssetsIds,
  //   },
  //   {
  //     campaignIds: params.campaignIds,
  //     emailIds: params.emailIds,
  //   },
  // );
  // addWorkflowsPayload(payload, params.workflowIds);
  payload.append('start_date', params.startDate);
  payload.append('end_date', params.endDate);
  if (params.accountId !== undefined) payload.append('account_id', params.accountId);
  if (params.contactsByFilter) payload.append('contacts_by_filter', params.contactsByFilter);
  if (params.exportAnalytics) payload.append('export_analytics', 'true');

  //Check if there are any previous pending requests
  if (typeof cancelTokenGetContactsCountService != typeof undefined) {
    cancelTokenGetContactsCountService &&
      cancelTokenGetContactsCountService.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenGetContactsCountService = axios.CancelToken.source();

  return await http.post(
    `${API_BASE_ANALYTICS}contact_listing_tab_counts/`,
    payload,
    { cancelToken: cancelTokenGetContactsCountService.token }, //Pass the cancel token to the current request
  );
};

let cancelTokenGetCampaignsAndWorkflowDropdownService: any;
export const getCampaignAndWorkflowDropdownService = async () => {
  const payload = {};
  //Check if there are any previous pending requests
  if (typeof cancelTokenGetCampaignsAndWorkflowDropdownService != typeof undefined) {
    cancelTokenGetCampaignsAndWorkflowDropdownService &&
      cancelTokenGetCampaignsAndWorkflowDropdownService.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenGetCampaignsAndWorkflowDropdownService = axios.CancelToken.source();
  return await http.post(
    `${API_BASE_ANALYTICS}campaign_workflow_dropdown/`,
    payload,
    { cancelToken: cancelTokenGetCampaignsAndWorkflowDropdownService.token }, //Pass the cancel token to the current request
  );
};

let cancelTokenGetEmailDropdownService: any;
export const getEmailDropdownService = async (params: any) => {
  const payload = {
    campaign_ids: params?.campaignIds ?? [],
    default: params?.default ?? false,
    all_campaigns: params?.allCampaigns ?? false,
    search_term: params?.emailSearchTerm ?? '',
    is_test_email: params?.isTestEmail ?? false,
    start_date: params.startDate,
    end_date: params.endDate,
    limit: params.limit,
    offset: params.offset,
    include_emails: params?.includeEmails ?? [],
    exclude_emails: params?.excludeEmails ?? [],
    unselect_all: params?.unselectAllEmailsFlag ?? false,
    select_all: params?.selectAllEmailsFlag ?? false,
    search_list: params.searchEmailList.length
      ? params.searchEmailList.map((item: ISearchedEmailDropdownList) => {
          return {
            term: item.term,
            select_all: item.selectAll,
            email_ids: item.emailIds.length ? item.emailIds.join().split(',') : [],
          };
        })
      : [],
    is_count: true,
    workflow_ids: params.workflowId,
    filter_id: params?.filterId ?? 0,
  };
  //Check if there are any previous pending requests
  if (typeof cancelTokenGetEmailDropdownService != typeof undefined) {
    cancelTokenGetEmailDropdownService &&
      cancelTokenGetEmailDropdownService.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenGetEmailDropdownService = axios.CancelToken.source();
  return await http.post(
    `${API_BASE_ANALYTICS}emails_dropdown/`,
    payload,
    { cancelToken: cancelTokenGetEmailDropdownService.token }, //Pass the cancel token to the current request
  );
};

let cancelTokenBuyerJourneyTrailService: any;
export const getBuyerJourneyTrailService = async (params: any) => {
  const url_params = objectToQueryParams(params);

  const url = `${API_BASE_BUYER_JOURNEY}buyer_journey_trail/?${url_params}`;
  if (typeof cancelTokenBuyerJourneyTrailService != typeof undefined) {
    cancelTokenBuyerJourneyTrailService &&
      cancelTokenBuyerJourneyTrailService.cancel('Operation canceled due to new request.');
  }
  //Save the cancel token for the current request
  cancelTokenBuyerJourneyTrailService = axios.CancelToken.source();
  return await http.get(url, {
    cancelToken: cancelTokenBuyerJourneyTrailService.token,
  });
};
